import { useLang } from 'hooks/lang';
import { useState } from 'react';

import { FullProjectIssue, loaders, isOwner, isIssueOpen } from 'entities';

import { Tabs, Row, ResponsiveOverlay } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { EntityStatusTag } from 'components/Entities';

import { CalendarRangeIcon } from 'components/Icons';

import { FinishIssue } from './Finish';
import { MainInfo } from './Main';
import { IssueEvents } from './Events';
import { IssueLink } from './IssueLink';
import { LoadingIssue } from './Loading';
import { IssueUsers } from './Users';

import { useUser } from 'hooks/user';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { formatDate } from 'utils/date';

import { IssueTab } from './types';

interface IssueModalProps {
  children?: never;
  issue?: FullProjectIssue | null;
  close: () => void;
  updateIssue: (newIssue: FullProjectIssue) => void;
}

const tabComponents: Record<IssueTab, typeof MainInfo> = {
  main: MainInfo,

  users: IssueUsers,

  logs: IssueEvents,

  finish: FinishIssue,
};

export function IssueModal({ close, issue, updateIssue }: IssueModalProps) {
  const { t } = useLang();
  const user = useUser();

  const { project } = useProjectProfile();

  const [tab, setTab] = useState<IssueTab>('main');

  const { errorOrLoading } = loaders.projects.findIssue.use({
    blockLoad: !issue,

    params: {
      projectId: issue?.project as string,
      issueId: issue?.id as string,
    },

    onDataChange: updateIssue,
  });

  const showLoading = !issue || errorOrLoading;

  const Component = tabComponents[tab];

  const fullyClose = (): void => {
    close();

    setTimeout(() => setTab('main'), 500);
  };

  return (
    <ResponsiveOverlay
      preventOpenAutoFocus
      allowEscape
      isOpen={!!issue}
      close={fullyClose}
      className="h-[min(800px,80vh)] w-[min(90vw,660px)] overflow-y-auto phone:w-full"
      title={
        issue ? (
          <Row gap="2">
            <h2 className="text-lg font-semibold leading-none tracking-tight">{issue.name}</h2>
            <IssueLink issue={issue} />
          </Row>
        ) : (
          ''
        )
      }
    >
      {showLoading ? (
        <LoadingIssue />
      ) : (
        <>
          <Row justifyContent="space-between">
            <Row gap="3">
              <UserAvatar user={issue!.owner} />

              <EntityStatusTag target="issues" status={issue.status} round={false} size="medium" />
            </Row>

            <Row gap="2">
              <CalendarRangeIcon size={20} color={'GrayText'} />

              <p className="text-sm">{formatDate(issue!.startDate)}</p>
            </Row>
          </Row>

          <Tabs
            value={tab}
            onTabChange={setTab as () => void}
            tabListClassName="-ml-2"
            tabs={[
              { value: 'main', label: t('main') },
              { value: 'users', label: t('users') },
              { value: 'logs', label: t('logs') },
              {
                value: 'finish',
                label: t('finish'),
                hide: !(
                  isIssueOpen(issue) && [issue, project].some((entity) => isOwner(entity, user))
                ),
              },
            ]}
          />

          {Component && (
            <Component close={fullyClose} issue={issue} setTab={setTab} updateIssue={updateIssue} />
          )}
        </>
      )}
    </ResponsiveOverlay>
  );
}
